import { ChatBubble } from "@/components/ai/chat/ChatBubble";
import { TodoContainer } from "@/components/ai/chat/TodoContainer";
import {
    type AgentMessageFragmentType,
    type AgentMessageFragment,
    type Message,
} from "@/hooks/use-stream-conversation";
import { type PlainAgentExecution } from "@/queries/agent-queries";
import { filterMessagesByType, MESSAGE_TYPES } from "@/utils/chat";
import { groupMessagesByTodo } from "@/utils/message-grouping";
import { AgentExecution_Phase, AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { type FC, useMemo } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { Fragment } from "@/components/ai/Fragment";

type GroupedMessagesProps = {
    messages: Message[];
    agentExecution: PlainAgentExecution;
    onClick?: (block: AgentMessageFragment) => void;
};

// Todo items are handled at the grouping level, so we don't
// want to render them as fragments
const IgnoreFragments = new Set<AgentMessageFragmentType>(["todoItem"]);

export const GroupedMessages: FC<GroupedMessagesProps> = ({ messages, agentExecution, onClick }) => {
    // Group messages by todo - MEMOIZED to prevent re-computation on every render
    // Groups are organized into blocks based on when todos were created together
    const groupedMessages = useMemo(() => {
        return groupMessagesByTodo(messages);
    }, [messages]);

    // Helper to check if previous/next group is a todo in the same block
    const isPreviousTodoInSameBlock = (index: number) => {
        if (index <= 0) return false;
        const current = groupedMessages[index];
        const prev = groupedMessages[index - 1];
        return current?.type === "todo-group" && prev?.type === "todo-group" && current.blockId === prev.blockId;
    };
    const isNextTodoInSameBlock = (index: number) => {
        if (index >= groupedMessages.length - 1) return false;
        const current = groupedMessages[index];
        const next = groupedMessages[index + 1];
        return current?.type === "todo-group" && next?.type === "todo-group" && current.blockId === next.blockId;
    };

    // Helper to get spacing class for an element
    const getSpacingClass = (index: number, isTodo: boolean) => {
        if (isTodo) {
            // Todo containers handle their own margins
            return "";
        }
        // Non-todo elements always get bottom margin unless they're the last item
        return index < groupedMessages.length - 1 ? "mb-4" : "";
    };

    const isDone =
        agentExecution.status?.phase === AgentExecution_Phase.WAITING_FOR_INPUT ||
        agentExecution.status?.phase === AgentExecution_Phase.STOPPED;

    const isNaturallyCompleted = isDone && agentExecution.spec?.desiredPhase !== AgentExecution_Phase.STOPPED;

    return groupedMessages.map((group, groupIndex) => {
        const isLastGroup = groupIndex === groupedMessages.length - 1;

        // Calculate fragment count for counter badge (steps within this todo)
        const fragmentCount = group.type === "todo-group" ? group.fragments.length : undefined;

        if (group.type === "user") {
            return (
                <div
                    key={group.message.id}
                    className={cn("flex w-full justify-end", getSpacingClass(groupIndex, false))}
                >
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={group.message}
                        onClick={onClick}
                        isLast={isLastGroup}
                        agentMessages={filterMessagesByType(messages, MESSAGE_TYPES.AGENT)}
                        data-tracking-id-none
                    />
                </div>
            );
        } else if (group.type === "agent") {
            // Render agent message with ChatBubble (includes feedback buttons)
            return (
                <div key={group.message.id} className={cn("w-full", getSpacingClass(groupIndex, false))}>
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={group.message}
                        onClick={onClick}
                        isLast={isLastGroup}
                        agentMessages={filterMessagesByType(messages, MESSAGE_TYPES.AGENT)}
                        data-tracking-id-none
                    />
                </div>
            );
        } else if (group.type === "todo-group") {
            // Use block-based properties for header and connectivity
            const hasPreviousTodoInBlock = isPreviousTodoInSameBlock(groupIndex);
            const hasNextTodoInBlock = isNextTodoInSameBlock(groupIndex);

            // Show header only for the first todo in a block
            const shouldShowTodoHeader = group.isFirstInBlock;

            // Find the current active fragment (last incomplete one when in progress)
            const isInProgress = group.todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS;
            const currentFragment =
                isInProgress && group.fragments.length > 0 ? group.fragments[group.fragments.length - 1] : null;

            // Extract text content for shimmer effect
            let currentFragmentText: string | undefined;
            if (isInProgress && group.fragments.length === 0) {
                // No fragments yet, Ona is thinking/planning
                currentFragmentText = "Thinking...";
            } else if (currentFragment && currentFragment.type === "text" && currentFragment.content) {
                currentFragmentText = currentFragment.content.split("\n")[0].substring(0, 100); // First line, max 100 chars
            } else if (currentFragment && currentFragment.type === "action" && currentFragment.intentDescription) {
                currentFragmentText = currentFragment.intentDescription;
            }

            return (
                <div key={group.todoId} className="w-full">
                    {shouldShowTodoHeader && (
                        <div className="mb-2 flex items-center gap-2">
                            <div className="rounded bg-surface-muted px-1.5 py-0.5 text-xs font-medium uppercase tracking-[0.5px] text-content-muted">
                                TODOs
                            </div>
                            <span className="text-sm font-medium text-content-muted">
                                {group.blockCompletedTodos}/{group.blockTotalTodos}
                            </span>
                        </div>
                    )}
                    <TodoContainer
                        todoId={group.todoId}
                        todoTitle={group.todoTitle}
                        todoStatus={group.todoStatus}
                        isLast={isLastGroup}
                        hasPreviousTodo={hasPreviousTodoInBlock}
                        hasNextTodo={hasNextTodoInBlock}
                        fragmentCount={fragmentCount}
                        fragments={group.fragments}
                        currentFragmentText={currentFragmentText}
                        pendingTodos={group.pendingTodos}
                        isLastInBlock={group.isLastInBlock}
                    >
                        <div className="flex flex-col gap-4">
                            {group.fragments.map((fragment, fragIndex) => {
                                const isLastFragment = fragIndex === group.fragments.length - 1;
                                const shouldHighlightFragment = isNaturallyCompleted && isLastFragment;
                                return (
                                    <Fragment
                                        key={`${group.todoId}-${fragIndex}`}
                                        fragment={fragment}
                                        agentExecution={agentExecution}
                                        shouldHighlightFragment={shouldHighlightFragment}
                                        onClick={onClick}
                                        data-tracking-id="default-tracking-id"
                                        ignore={IgnoreFragments}
                                    />
                                );
                            })}
                        </div>
                    </TodoContainer>
                </div>
            );
        } else if (group.type === "agent-ungrouped") {
            // Render ungrouped fragments with ChatBubble wrapper (includes feedback buttons)
            const syntheticMessage: Message = {
                type: "agent",
                id: group.messageId,
                fragments: group.fragments,
            };
            return (
                <div key={`ungrouped-${groupIndex}`} className={cn("w-full", getSpacingClass(groupIndex, false))}>
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={syntheticMessage}
                        onClick={onClick}
                        isLast={isLastGroup}
                        agentMessages={filterMessagesByType(messages, MESSAGE_TYPES.AGENT)}
                        data-tracking-id-none
                    />
                </div>
            );
        }
        return null;
    });
};
